import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDivider,
  IgLink,
  IgPaper,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { Grid, StepIcon } from "@mui/material";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { useGetExperiencesList } from "../../../api/experiences-list/get-experiences-list";
import { useGetOrg } from "../../../api/organizations/get-organization";
import { useGetScriptStatus } from "../../../api/shops/get-script-status";
import { CAMPAIGN_ROUTES, EXPERIMENT_ROUTES } from "../../../routes/routes";
import { OnboardingFlowHelperSteps } from "../../../types/organization";
import {
  bookDemo,
  campaignCheckoutScript,
  campaignProgressBarSetup,
  campaignSetup,
  campaignTestSetup,
  contentTestSetup,
  priceFunctionsIntegration,
  priceNonPlusIntegration,
  priceShopPlusIntegration,
  priceTestSetup,
  quantityButtonSetup,
  shippingTestSetup,
} from "../../../utils/linkSheet";
import openNewTab from "../../../utils/openNewTab";

import { isSelectedTestTypeNotCreated, previewModeLink } from "./utils";

export const OnboardingTestFlowHelper = () => {
  const navigate = useNavigate();
  const org = useGetOrg();
  const [scriptStatusFailed, setScriptStatusFailed] = useImmer<boolean>(false);

  const scriptStatus = useGetScriptStatus(
    scriptStatusFailed ? undefined : org.data?.org?.id,
    undefined,
    undefined,
    {
      refetchOnWindowFocus: true,
    }
  );

  useEffect(() => {
    if (scriptStatus.isError) {
      setScriptStatusFailed(true);
    }
  }, [scriptStatus.isError]);

  const selectedTestType = useConfiguration<string>("onboarding_test_flow");
  const shopifyFunctionsEnabled = useConfiguration<boolean>(
    "shopify_functions_enabled"
  );
  const shopifyPlan = useConfiguration("shopify_plan");
  const iShopifyFunctionsPricingEnabled = useConfiguration(
    "shopify_functions_for_pricing_enabled"
  );
  const experimentsList = useGetExperiencesList({ category: "experiment" });
  const campaigns = useGetExperiencesList({ category: "personalization" });

  const onPreviewClick = useMemo(() => {
    if (
      experimentsList.isSuccess &&
      campaigns.isSuccess &&
      selectedTestType?.value
    ) {
      return previewModeLink(
        experimentsList.data.experiencesList,
        campaigns.data.experiencesList,
        selectedTestType!.value,
        org.data?.org.domain?.replace("https://", "") ?? org.data.org.shopId!
      );
    }
    return false;
  }, [experimentsList, org, selectedTestType]);

  const canPreviewTest = useMemo(() => {
    if (
      scriptStatus.isSuccess &&
      experimentsList.isSuccess &&
      campaigns.isSuccess &&
      selectedTestType?.value
    ) {
      return (
        scriptStatus.data.exists &&
        !isSelectedTestTypeNotCreated(
          experimentsList.data.experiencesList,
          campaigns.data.experiencesList,
          selectedTestType!.value
        )
      );
    } else return false;
  }, [scriptStatus, experimentsList, campaigns]);

  //#region -- General Steps --
  const generalStepOne: OnboardingFlowHelperSteps = {
    title: (
      <IgTypography igStyle={"semibold"}>
        Enable Intelligems in your theme.
      </IgTypography>
    ),
    buttonText: "Enable",
    buttonFunction: () => {
      openNewTab(
        `${
          org?.data?.org?.shopId
        }/admin/themes/current/editor?context=apps&activateAppId=${
          import.meta.env.VITE_SHOPIFY_THEME_BLOCKS_UUID
        }/intelligems-script`,
        undefined,
        true
      );
    },
    isComplete: scriptStatus.isSuccess && scriptStatus.data.exists,
    isEnabled: true,
  };

  const generalPreviewStep: OnboardingFlowHelperSteps = {
    title: (
      <IgTypography igStyle={"semibold"}>
        Preview your test before it goes live.
      </IgTypography>
    ),
    buttonText: "Open Preview Mode",
    buttonFunction: () => {
      onPreviewClick
        ? openNewTab(onPreviewClick as string, undefined, true)
        : null;
    },
    isComplete: false,
    isEnabled: canPreviewTest,
    disabledText:
      scriptStatus.isSuccess && !scriptStatus.data.exists
        ? "Enable Intelligems in your theme before previewing your test."
        : "Please create an experiment first before previewing.",
  };
  //#endregion
  //#region -- Content Test Steps --
  const contentTestSteps: OnboardingFlowHelperSteps[] = [
    generalStepOne,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Set up your first content test.
          <IgTypography>
            {" "}
            See our helpful walkthrough{" "}
            <IgLink
              onClick={() => openNewTab(contentTestSetup, "Content Test Setup")}
            >
              here.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      buttonText: "Create Test",
      buttonFunction: () => {
        navigate(
          `${EXPERIMENT_ROUTES.ContentTest}/?action=new&type=content/advanced`
        );
      },
      isComplete:
        experimentsList.isSuccess &&
        experimentsList.data.experiencesList.filter(
          (exp) => exp.testTypes.hasTestContent
        ).length > 0,
      isEnabled: true,
    },
    generalPreviewStep,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Launch your test!{" "}
          <IgTypography>
            Click “start” on the test from the{" "}
            <IgLink
              onClick={() => {
                navigate(EXPERIMENT_ROUTES.Experiences);
              }}
            >
              experiments page.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      isComplete: true,
      isEnabled: true,
    },
  ];
  //#endregion
  //#region -- Campaign Test Steps --
  const checkoutScriptStep = {
    title: (
      <IgTypography igStyle={"semibold"}>
        Add the Intelligems checkout script to you Shopify store.{" "}
        <IgTypography>
          Learn more{" "}
          <IgLink
            onClick={() => {
              openNewTab(campaignCheckoutScript, "Campaign Checkout Script");
            }}
          >
            here.
          </IgLink>
        </IgTypography>
      </IgTypography>
    ),
    isComplete: false,
    isEnabled: true,
  };

  const campaignTestSteps: OnboardingFlowHelperSteps[] = [
    generalStepOne,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Set up your first{" "}
          <IgLink
            onClick={() => {
              navigate(`${CAMPAIGN_ROUTES.Campaign}/?action=new`);
            }}
          >
            campaign
          </IgLink>{" "}
          or{" "}
          <IgLink
            onClick={() => {
              navigate(`${EXPERIMENT_ROUTES.OfferTest}/?action=new`);
            }}
          >
            campaign test.
          </IgLink>
          <IgTypography>
            {" "}
            See our helpful walkthroughs on{" "}
            <IgLink
              onClick={() => {
                openNewTab(campaignSetup, "Campaign Setup");
              }}
            >
              campaigns
            </IgLink>{" "}
            and{" "}
            <IgLink
              onClick={() => {
                openNewTab(campaignTestSetup, "Campaign Test Setup");
              }}
            >
              campaign tests.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      isComplete: false,
      isEnabled: true,
    },
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Add{" "}
          <IgLink
            onClick={() => {
              openNewTab(quantityButtonSetup, "Quantity Button Integration");
            }}
          >
            quantity buttons
          </IgLink>{" "}
          and/or a{" "}
          <IgLink
            onClick={() => {
              openNewTab(campaignProgressBarSetup, "Progress Bar Integration");
            }}
          >
            progress bar
          </IgLink>{" "}
          to you Shopify theme. (Optional)
        </IgTypography>
      ),
      isComplete: false,
      isEnabled: true,
    },
    generalPreviewStep,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Launch your campaign!{" "}
          <IgTypography>
            Click the toggle on the campaign from the{" "}
            <IgLink
              onClick={() => {
                navigate(CAMPAIGN_ROUTES.CampaignOverview);
              }}
            >
              campaigns page
            </IgLink>{" "}
            or press "start" from the{" "}
            <IgLink
              onClick={() => {
                navigate(EXPERIMENT_ROUTES.Experiences);
              }}
            >
              experiments page.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      isComplete: true,
      isEnabled: true,
    },
  ];

  const finalCampaignTestSteps = useMemo(() => {
    const finalSteps = campaignTestSteps;
    if (shopifyFunctionsEnabled?.value) {
      finalSteps.splice(3, 0, checkoutScriptStep);
    }
    return finalSteps;
  }, [shopifyFunctionsEnabled]);
  //#endregion
  //#region -- Pricing Test Steps --
  const priceTestSteps: OnboardingFlowHelperSteps[] = [
    generalStepOne,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Book a demo! (Optional){" "}
          <IgTypography>
            Our team would be happy to walk you through the best practices and{" "}
            <IgLink
              onClick={() => {
                openNewTab(bookDemo, "Book Demo");
              }}
            >
              help you get set up.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      buttonText: "Enable",
      buttonFunction: () => {
        openNewTab(
          `${
            org?.data?.org.shopId
          }/admin/themes/current/editor?context=apps&activateAppId=${
            import.meta.env.VITE_SHOPIFY_THEME_BLOCKS_UUID
          }/intelligems-script`,
          undefined,
          true
        );
      },
      isComplete: true,
      isEnabled: true,
    },
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Set up your first pricing test.
          <IgTypography>
            {" "}
            See our helpful walkthrough{" "}
            <IgLink
              onClick={() => openNewTab(priceTestSetup, "Price Test Setup")}
            >
              here.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      buttonText: "Create Test",
      buttonFunction: () => {
        navigate(`${EXPERIMENT_ROUTES.PriceTest}/?action=new`);
      },
      isComplete:
        experimentsList.isSuccess &&
        experimentsList.data.experiencesList.filter(
          (exp) => exp.testTypes.hasTestPricing
        ).length > 0,
      isEnabled: true,
    },
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Complete the pricing integration.
          <IgTypography>
            {" "}
            Learn how to do a{" "}
            <IgLink
              onClick={() =>
                openNewTab(
                  shopifyFunctionsEnabled?.value === true
                    ? priceFunctionsIntegration
                    : shopifyPlan?.value === "shopify_plus"
                      ? priceShopPlusIntegration
                      : priceNonPlusIntegration,
                  shopifyFunctionsEnabled?.value === true
                    ? "Shopify Functions Pricing Integration"
                    : shopifyPlan?.value === "shopify_plus"
                      ? "Shopify Plus Pricing Integration"
                      : "Non Shopify Plus Pricing Integration"
                )
              }
            >
              pricing integration
            </IgLink>{" "}
            or{" "}
            <IgLink
              onClick={() => window?.HubSpotConversations?.widget?.open()}
            >
              contact us
            </IgLink>{" "}
            for help.
          </IgTypography>
        </IgTypography>
      ),
      isComplete: true,
      isEnabled: true,
    },
    generalPreviewStep,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Launch your test!{" "}
          <IgTypography>
            Click “start” on the test from the{" "}
            <IgLink
              onClick={() => {
                navigate(EXPERIMENT_ROUTES.Experiences);
              }}
            >
              experiments page.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      isComplete: true,
      isEnabled: true,
    },
  ];
  //#endregion
  //#region -- Shipping Test Steps --
  const shippingTestSteps: OnboardingFlowHelperSteps[] = [
    generalStepOne,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Book a demo! (Optional){" "}
          <IgTypography>
            Our team would be happy to walk you through the best practices and{" "}
            <IgLink
              onClick={() => {
                openNewTab(bookDemo, "Book Demo");
              }}
            >
              help you get set up.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      isComplete: true,
      isEnabled: true,
    },
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Set up your first shipping test.
          <IgTypography>
            {" "}
            See our helpful walkthrough{" "}
            <IgLink
              onClick={() =>
                openNewTab(
                  shippingTestSetup,
                  "Shipping Testing - Getting Started"
                )
              }
            >
              here.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      buttonText: "Create Test",
      buttonFunction: () => {
        navigate(`${EXPERIMENT_ROUTES.ShippingTest}/?action=new`);
      },
      isComplete:
        experimentsList.isSuccess &&
        experimentsList.data.experiencesList.filter(
          (exp) => exp.testTypes.hasTestShipping
        ).length > 0,
      isEnabled: true,
    },
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Complete the shipping integration.
          <IgTypography>
            {" "}
            Learn how to do a{" "}
            <IgLink
              onClick={() =>
                openNewTab(
                  shippingTestSetup,
                  "Shipping Testing - Getting Started"
                )
              }
            >
              shipping integration
            </IgLink>{" "}
            or{" "}
            <IgLink
              onClick={() => window?.HubSpotConversations?.widget?.open()}
            >
              contact us
            </IgLink>{" "}
            for help.
          </IgTypography>
        </IgTypography>
      ),
      isComplete: true,
      isEnabled: true,
    },
    generalPreviewStep,
    {
      title: (
        <IgTypography igStyle={"semibold"}>
          Launch your test!{" "}
          <IgTypography>
            Click “start” on the test from the{" "}
            <IgLink
              onClick={() => {
                navigate(EXPERIMENT_ROUTES.Experiences);
              }}
            >
              experiments page.
            </IgLink>
          </IgTypography>
        </IgTypography>
      ),
      isComplete: true,
      isEnabled: true,
    },
  ];
  //#endregion

  const testSteps =
    selectedTestType?.value === "campaign"
      ? finalCampaignTestSteps
      : selectedTestType?.value === "content"
        ? contentTestSteps
        : selectedTestType?.value === "pricing"
          ? priceTestSteps
          : shippingTestSteps;

  return (
    <IgPaper>
      <Grid
        container
        direction={"column"}
        columnSpacing={2}
        rowSpacing={2}
        justifyContent={"center"}
      >
        <Grid item>
          <IgTypography igStyle={"semibold"} sx={{ fontSize: "22px" }}>
            Let's get started on your{" "}
            {selectedTestType?.value === "campaign"
              ? "offer test"
              : selectedTestType?.value === "content"
                ? "content test"
                : selectedTestType?.value === "pricing"
                  ? "pricing test"
                  : "shipping test"}{" "}
            setup
          </IgTypography>
        </Grid>
        <Grid item container direction={"column"}>
          {testSteps.map((step, index) => (
            <div key={index}>
              <Grid
                item
                container
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Grid item container direction={"row"} flex={1}>
                  <StepIcon
                    active={true}
                    icon={index + 1}
                    sx={{
                      marginRight: "10px",
                    }}
                  />
                  <>{step.title}</>
                </Grid>
                {step.buttonText && !step.isComplete && (
                  <Grid item>
                    <IgTooltip
                      title={
                        step.isEnabled
                          ? ""
                          : step.disabledText ||
                            "Please complete the previous steps"
                      }
                    >
                      <IgButton
                        onClick={() => {
                          step.buttonFunction && step.buttonFunction();
                        }}
                        disabled={!step.isEnabled}
                        variant={"contained"}
                      >
                        {step.buttonText}
                      </IgButton>
                    </IgTooltip>
                  </Grid>
                )}
              </Grid>
              {index !== testSteps.length - 1 && (
                <Grid item>
                  <IgDivider spacing={"m"} />
                </Grid>
              )}
            </div>
          ))}
        </Grid>
      </Grid>
    </IgPaper>
  );
};
